import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';

import { IconComponent } from '../icon/icon.component';
import { Star } from '../icon/icons';
import { RadioGroupComponent, RadioItem, UI_RADIO_GROUP, UI_RADIO_ITEM } from './radio-group.component';

/** Una estrella. Interna de `ui-star-rating`. */
@Component({
  selector: 'ui-star',
  imports: [IconComponent],
  template: `
    <button
      #control
      type="button"
      role="radio"
      class="star"
      [class.filled]="filled()"
      [attr.aria-checked]="group.value() === value()"
      [attr.aria-label]="label()"
      [attr.tabindex]="group.tabbable() === value() ? 0 : -1"
      (click)="group.select(this)"
    >
      <ui-icon [icon]="icon" size="1.75rem" [strokeWidth]="1.75" />
    </button>
  `,
  styleUrl: './star-rating.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: UI_RADIO_ITEM, useExisting: forwardRef(() => StarComponent) }],
})
export class StarComponent implements RadioItem {
  // Ver la nota en RadioRowComponent: no es `required` porque va dentro de un @for.
  readonly value = input('');
  readonly label = input('');
  readonly filled = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });

  protected readonly group = inject(UI_RADIO_GROUP);
  protected readonly icon = Star;

  private readonly control = viewChild.required<ElementRef<HTMLButtonElement>>('control');

  focus(): void {
    this.control().nativeElement.focus();
  }
}

/**
 * Calificación de 1 a 5. Es un radio group: se elige con un toque y con flechas, y cada estrella se anuncia
 * con su número. A la vista también va el número ("3 de 5"): el relleno no debe ser la única señal.
 */
@Component({
  selector: 'ui-star-rating',
  imports: [RadioGroupComponent, StarComponent],
  template: `
    <div class="head">
      <span class="label">{{ label() }}</span>
      @if (value()) {
        <span class="count">{{ value() }} de {{ max }}</span>
      }
    </div>
    <ui-radio-group class="stars" [label]="label()" [value]="selected()" (valueChange)="value.set(toNumber($event))">
      @for (star of stars; track star) {
        <ui-star [value]="'' + star" [label]="star === 1 ? '1 estrella' : star + ' estrellas'" [filled]="star <= value()" />
      }
    </ui-radio-group>
    @if (error(); as message) {
      <p class="error">{{ message }}</p>
    }
  `,
  styleUrl: './star-rating.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StarRatingComponent {
  readonly label = input.required<string>();
  /** 0 = sin calificar. */
  readonly value = model(0);
  readonly error = input<string | null>();

  protected readonly max = 5;
  protected readonly stars = [1, 2, 3, 4, 5];
  protected readonly selected = computed(() => (this.value() ? String(this.value()) : null));

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  /** Para llevar el foco a la primera fila sin calificar (WCAG 3.3.1). */
  focus(): void {
    this.host.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')?.focus();
  }

  protected toNumber(value: string | null): number {
    return Number(value) || 0;
  }
}
